import {CollisionDetection, KeyboardCoordinateGetter, UniqueIdentifier} from '@dnd-kit/core';
import {hasSortableData} from '@dnd-kit/sortable';
import {match, Pattern} from 'ts-pattern';

import {isNil, isNotNil, prop} from 'ramda';
import {isNonEmptyArray} from 'ramda-adjunct';

import {DashboardGridRect} from '../types/DashboardGridRect';
import {DashboardTileId} from '../types/DashboardTileId';
import {PositionedDashboardGridTile} from './allocateDashboardGrid';
import {dashboardCollisionDetection} from './dashboardCollisionDetection';
import {DASHBOARD_TILE_SIZES} from './dashboardTileSizes';
import {getDashboardGapSlots} from './getDashboardGapSlots';
import {isDashboardTileFullWidth} from './isDashboardTileFullWidth';
import {parseDashboardGapSlotId} from './parseDashboardGapSlotId';
import {projectDashboardDrop, projectDashboardGapDrop} from './projectDashboardDrop';
import {TileDropPlacement} from './resolveTileDropPlacement';
import {toDashboardGapSlotId} from './toDashboardGapSlotId';

interface KeyboardSortStep {
  overId: UniqueIdentifier;
  placement: TileDropPlacement;
  destinationRow: number | null;
}

interface KeyboardSortCandidate {
  id: UniqueIdentifier;
  placement: TileDropPlacement;
  landingIndex: number;
  destinationRow: number | null;
  score: number;
}

export interface DashboardKeyboardSorting {
  coordinateGetter: KeyboardCoordinateGetter;
  collisionDetection: CollisionDetection;
  getOverId: () => UniqueIdentifier | null;
  getPlacement: () => TileDropPlacement | null;
  getProjectedIndex: () => number | null;
  reset: () => void;
  setTiles: (tiles: PositionedDashboardGridTile[]) => void;
}

interface ArrowStep {
  column: number;
  row: number;
}

const ARROW_STEPS: Record<string, ArrowStep | undefined> = {
  ArrowRight: {column: 1, row: 0},
  ArrowDown: {column: 0, row: 1},
  ArrowLeft: {column: -1, row: 0},
  ArrowUp: {column: 0, row: -1},
};

const PRIMARY_AXIS_WEIGHT = 100;
const KEYBOARD_COLLISION_RANK = 0;
const WIDE_PLACEMENTS: TileDropPlacement[] = ['before', 'after'];

const getTileRect = (tile: PositionedDashboardGridTile): DashboardGridRect => ({
  column: tile.column,
  row: tile.row,
  ...prop(tile.size, DASHBOARD_TILE_SIZES),
});

const getRectCenter = (rect: DashboardGridRect) => ({
  column: rect.column + rect.columns / 2,
  row: rect.row + rect.rows / 2,
});

const getCandidateScore = (
  current: DashboardGridRect,
  candidate: DashboardGridRect,
  arrow: ArrowStep
) => {
  const currentCenter = getRectCenter(current);
  const candidateCenter = getRectCenter(candidate);
  const columnDistance = candidateCenter.column - currentCenter.column;
  const rowDistance = candidateCenter.row - currentCenter.row;
  const primaryDistance = columnDistance * arrow.column + rowDistance * arrow.row;

  if (primaryDistance <= 0) {
    return null;
  }

  const crossDistance = Math.abs(columnDistance * arrow.row - rowDistance * arrow.column);

  return primaryDistance * PRIMARY_AXIS_WEIGHT + crossDistance;
};

// Where the keyboard drag currently is: the source tile, a target tile or a free place.
const getCurrentRect = (
  tiles: PositionedDashboardGridTile[],
  active: PositionedDashboardGridTile,
  overId: UniqueIdentifier | null
): DashboardGridRect => {
  const activeRect = getTileRect(active);
  const gap = isNil(overId) ? null : parseDashboardGapSlotId(overId);

  if (gap !== null) {
    return {...activeRect, ...gap};
  }

  const overTile = tiles.find((tile) => tile.id === overId);

  return overTile === undefined ? activeRect : getTileRect(overTile);
};

const getTileCandidates = (
  tiles: PositionedDashboardGridTile[],
  active: PositionedDashboardGridTile,
  current: DashboardGridRect,
  arrow: ArrowStep
): KeyboardSortCandidate[] =>
  tiles.flatMap((candidate) => {
    const score = getCandidateScore(current, getTileRect(candidate), arrow);

    if (score === null) {
      return [];
    }

    if (candidate.id === active.id) {
      return [
        {
          id: candidate.id,
          placement: 'swap' as const,
          landingIndex: tiles.indexOf(active),
          destinationRow: null,
          score,
        },
      ];
    }

    const transaction = projectDashboardDrop(tiles, active.id, candidate.id, 'swap');

    return transaction === null
      ? []
      : [
          {
            id: candidate.id,
            placement: 'swap' as const,
            landingIndex: transaction.landingIndex,
            destinationRow: null,
            score,
          },
        ];
  });

const getGapCandidates = (
  tiles: PositionedDashboardGridTile[],
  active: PositionedDashboardGridTile,
  current: DashboardGridRect,
  arrow: ArrowStep
): KeyboardSortCandidate[] =>
  getDashboardGapSlots(tiles, active.id).flatMap((slot) => {
    const score = getCandidateScore(current, slot, arrow);

    if (score === null) {
      return [];
    }

    const transaction = projectDashboardGapDrop(tiles, active.id, slot);

    return transaction === null
      ? []
      : [
          {
            id: toDashboardGapSlotId(slot),
            placement: 'swap' as const,
            landingIndex: transaction.landingIndex,
            destinationRow: null,
            score,
          },
        ];
  });

// Tiles come first, so the stable sort lets a tile win a tie with a free place.
const getNarrowCandidates = (
  tiles: PositionedDashboardGridTile[],
  active: PositionedDashboardGridTile,
  current: DashboardGridRect,
  arrow: ArrowStep
): KeyboardSortCandidate[] =>
  [
    ...getTileCandidates(tiles, active, current, arrow),
    ...getGapCandidates(tiles, active, current, arrow),
  ].sort((first, second) => first.score - second.score);

const getWideCandidates = (
  tiles: PositionedDashboardGridTile[],
  active: PositionedDashboardGridTile,
  currentRow: number,
  arrow: ArrowStep
): KeyboardSortCandidate[] => {
  const sourceDistance = (active.row - currentRow) * arrow.row;
  const sourceCandidate: KeyboardSortCandidate[] =
    sourceDistance > 0
      ? [
          {
            id: active.id,
            placement: 'before',
            landingIndex: tiles.indexOf(active),
            destinationRow: active.row,
            score: sourceDistance,
          },
        ]
      : [];
  const candidatesByDestination = new Map<number, KeyboardSortCandidate>();

  tiles.forEach((candidate) => {
    if (candidate.id === active.id) {
      return;
    }

    WIDE_PLACEMENTS.forEach((placement) => {
      const transaction = projectDashboardDrop(tiles, active.id, candidate.id, placement);

      if (transaction?.operation.kind !== 'move-full-width-band') {
        return;
      }

      const destinationRow = transaction.operation.destinationRow;
      const distance = (destinationRow - currentRow) * arrow.row;

      if (distance <= 0 || candidatesByDestination.has(destinationRow)) {
        return;
      }

      candidatesByDestination.set(destinationRow, {
        id: candidate.id,
        placement,
        landingIndex: transaction.landingIndex,
        destinationRow,
        score: distance,
      });
    });
  });

  return [...sourceCandidate, ...candidatesByDestination.values()].sort(
    (first, second) => first.score - second.score
  );
};

export function createDashboardKeyboardSorting(): DashboardKeyboardSorting {
  let step: KeyboardSortStep | null = null;
  let projectedIndex: number | null = null;
  let activeId: DashboardTileId | null = null;
  let tiles: PositionedDashboardGridTile[] = [];

  const coordinateGetter: KeyboardCoordinateGetter = (event, {context}) => {
    const arrowStep = ARROW_STEPS[event.code];

    if (isNil(arrowStep)) {
      return undefined;
    }

    event.preventDefault();

    const {active, collisionRect, droppableRects} = context;
    const nextActiveId = active?.id.toString();

    if (isNil(active) || isNil(collisionRect) || !hasSortableData(active) || isNil(nextActiveId)) {
      return undefined;
    }

    const activeTile = tiles.find((tile) => tile.id === nextActiveId);

    if (activeTile === undefined) {
      return undefined;
    }

    const isWide = isDashboardTileFullWidth(activeTile.size);

    if (isWide && arrowStep.column !== 0) {
      return undefined;
    }

    if (activeId !== activeTile.id) {
      activeId = activeTile.id;
      projectedIndex = tiles.findIndex((tile) => tile.id === activeTile.id);
      step = null;
    }

    const currentRow = step?.destinationRow ?? activeTile.row;
    const candidates = isWide
      ? getWideCandidates(tiles, activeTile, currentRow, arrowStep)
      : getNarrowCandidates(
          tiles,
          activeTile,
          getCurrentRect(tiles, activeTile, step?.overId ?? null),
          arrowStep
        );
    const next = candidates[0];

    if (next === undefined) {
      return undefined;
    }

    projectedIndex = next.landingIndex;

    if (next.id === activeTile.id) {
      step = null;
      return {x: collisionRect.left, y: collisionRect.top};
    }

    step = {
      overId: next.id,
      placement: next.placement,
      destinationRow: next.destinationRow,
    };

    return match(droppableRects.get(next.id))
      .with(Pattern.nonNullable, (rect) => ({
        x: rect.left + (rect.width - collisionRect.width) / 2,
        y: rect.top + (rect.height - collisionRect.height) / 2,
      }))
      .otherwise(() => ({x: collisionRect.left, y: collisionRect.top}));
  };

  const collisionDetection: CollisionDetection = (args) => {
    const pointerCollisions = dashboardCollisionDetection(args);

    if (isNonEmptyArray(pointerCollisions) || isNotNil(args.pointerCoordinates)) {
      return pointerCollisions;
    }

    const keyboardOverId = step?.overId;
    const droppableContainer = args.droppableContainers.find(
      (candidate) => candidate.id === keyboardOverId
    );

    return isNil(droppableContainer)
      ? []
      : [{id: droppableContainer.id, data: {droppableContainer, value: KEYBOARD_COLLISION_RANK}}];
  };

  return {
    coordinateGetter,
    collisionDetection,
    getOverId: () => step?.overId ?? null,
    getPlacement: () => step?.placement ?? null,
    getProjectedIndex: () => projectedIndex,
    reset: () => {
      step = null;
      projectedIndex = null;
      activeId = null;
    },
    setTiles: (nextTiles) => {
      tiles = nextTiles;
    },
  };
}
